import type {Delimiter, Parent} from 'mdast'
import type {Extension, Handle} from 'mdast-util-from-markdown'

/**
 * Nested blockquotes are parsed as a blockquote immediately preceded by the prefixes from the parent blockquote,
 * which results in a broken document structure for the user because the nested `<blockquote>` element would
 * start in-between two prefix tokens:
 * ```html
 * <blockquote>
 *   <span class="delimiter">&gt;</span> outer quote
 *   <span class="delimiter">&gt;</span>
 *   <blockquote>
 *     <span class="delimiter">&gt;</span> inner quote
 *   </blockquote>
 * </blockquote>
 * ```
 * To work around this, we modify nested blockquotes to 'consume' any immediately preceding prefixes by expanding the
 * start of the blockquote backward. This gives us a more usable structure when transformed to HTML:
 * ```html
 * <blockquote>
 *   <span class="delimiter">&gt;</span> outer quote
 *   <blockquote>
 *     <span class="delimiter">&gt;</span><span class="delimiter">&gt;</span> inner quote
 *   </blockquote>
 * </blockquote>
 * ```
 */
export function mdastExpandNestedBlockquotes(): Extension {
  return {
    exit: {
      blockQuote: handleExitBlockquote,
    },
  }
}

/**
 * Upon exiting a blockquote, look through the parent's children for any immediately preceding `delimiter` nodes with
 * the `blockQuoteMarker` token type. If found, move them inside the `blockquote` node and adjust the node's start position.
 */
const handleExitBlockquote: Handle = function (this, token) {
  // -2 because all the delimiters we want to consume are on the parent of the quote node, which is still open for now
  const parent = this.stack.at(-2) as Parent | undefined
  if (!parent) return false

  // Get the quote node from the parent. Popping it off the array makes it easier to pop all the delimiters before it,
  // even though we'll have to push it back later
  const quoteNode = parent?.children?.pop()
  if (quoteNode?.type !== 'blockquote')
    throw new Error(`Expected open blockquote on stack, but found ${quoteNode?.type || 'undefined'}`)

  // Collect all the immediately preceding marker delimiters into an array while removing them from the parent
  const delimitersToConsume: Delimiter[] = []
  let previousSibling = parent.children.at(-1)
  while (previousSibling?.type === 'delimiter' && previousSibling.tokenType === 'blockQuoteMarker') {
    delimitersToConsume.push(previousSibling)
    parent.children.pop() // remove it from the parent
    previousSibling = parent.children.at(-1)
  }
  delimitersToConsume.reverse() // we added them from last to first, so we must reverse them to fix the order

  // Prepend the delimiters to the beginning of the quote node
  quoteNode.children ??= []
  quoteNode.children.unshift(...delimitersToConsume)

  // Update the quote node's start position
  if (quoteNode.position && delimitersToConsume[0]?.position) {
    quoteNode.position.start = delimitersToConsume[0].position.start
    // We also have to update the token because, internally, mdast will revert the node position if we don't
    Object.assign(token.start, delimitersToConsume[0].position.start)
  }

  // Add the quote node back to the tree
  parent.children.push(quoteNode)

  return false
}
